import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import { apiFetch } from '@/lib/operator-api/base-client';
import {
  CustomerCRM,
  CustomerTier,
  getEngagementColor,
  getTierColorClasses,
  CRMDashboardStats,
} from '@/lib/crm/client-types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Loader2, Search, Users, Lock, ChevronLeft, ChevronRight, Mail, Phone, Star, AlertTriangle, TrendingUp, UserPlus, Plus } from 'lucide-react';

/**
 * Mask phone number for display
 */
function maskPhone(phone: string, showFull: boolean): string {
  if (showFull) return phone;
  const parts = phone.split(' ');
  if (parts.length >= 3) {
    const lastPart = parts[parts.length - 1];
    const masked = lastPart.replace(/^(\d)(\d{4})(-?\d{4})$/, '$1****$3');
    parts[parts.length - 1] = masked;
    return parts.join(' ');
  }
  return phone.replace(/(\d{5})(\d{4})$/, '*****$2');
}

/**
 * Mask name for display
 */
function maskName(name: string, showFull: boolean): string {
  if (showFull) return name;
  const parts = name.split(' ');
  if (parts.length > 1) {
    return `${parts[0]} ${parts[1].charAt(0)}.`;
  }
  return `${name.charAt(0)}***`;
}

function OperatorCustomersPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { operator, permissions, isLoading: authLoading, selectedBusinessId, selectedBusiness } = useOperator();
  const [customers, setCustomers] = useState<CustomerCRM[]>([]);
  const [stats, setStats] = useState<CRMDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [tierFilter, setTierFilter] = useState<CustomerTier | 'all'>('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', phone: '', notes: '' });
  const [createError, setCreateError] = useState<string | null>(null);

  const handleCreateCustomer = async () => {
    if (!createForm.phone || !selectedBusinessId) return;
    setCreateLoading(true);
    setCreateError(null);
    try {
      const phone = createForm.phone.startsWith('+') ? createForm.phone : `+55${createForm.phone}`;
      const response = await apiFetch('/api/operator/customers', {
        method: 'POST',
        body: JSON.stringify({ businessId: selectedBusinessId, phone, name: createForm.name || undefined }),
      });
      const data = await response.json();
      if (data.success) {
        setIsCreateOpen(false);
        setCreateForm({ name: '', phone: '', notes: '' });
        setCreateError(null);
        // Refresh customers
        const params = new URLSearchParams();
        if (selectedBusinessId) params.set('businessId', selectedBusinessId);
        params.set('includeStats', 'true');
        const res = await apiFetch(`/api/operator/customers?${params}`);
        const freshData = await res.json();
        if (freshData.success) {
          setCustomers(freshData.customers.map((c: Record<string, unknown>) => ({
            ...c,
            firstBookingAt: c.firstBookingAt ? new Date(c.firstBookingAt as string) : null,
            lastBookingAt: c.lastBookingAt ? new Date(c.lastBookingAt as string) : null,
            lastInteraction: c.lastInteraction ? new Date(c.lastInteraction as string) : null,
            createdAt: c.createdAt ? new Date(c.createdAt as string) : new Date(),
          })));
        }
      } else {
        const msg = data.error || 'Failed to create customer';
        console.error('Create customer failed:', msg);
        setCreateError(msg);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create customer';
      console.error('Create customer error:', err);
      setCreateError(msg);
    } finally {
      setCreateLoading(false);
    }
  };

  // Check if user can view full PII
  const canViewFullPII = permissions?.canViewFullPII ?? false;

  // Load customers on mount and when business changes
  useEffect(() => {
    if (!operator) return;

    const loadCustomers = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (selectedBusinessId) params.set('businessId', selectedBusinessId);
        params.set('includeStats', 'true');

        const response = await apiFetch(`/api/operator/customers?${params.toString()}`);
        const data = await response.json();

        if (!response.ok || !data.success) {
          const errMsg = typeof data.error === 'string' ? data.error : data.error?.message || 'Failed to load customers';
          setError(errMsg);
          return;
        }

        // Parse dates from ISO strings
        const parsedCustomers: CustomerCRM[] = data.customers.map((c: Record<string, unknown>) => ({
          ...c,
          firstBookingAt: c.firstBookingAt ? new Date(c.firstBookingAt as string) : null,
          lastBookingAt: c.lastBookingAt ? new Date(c.lastBookingAt as string) : null,
          lastInteraction: c.lastInteraction ? new Date(c.lastInteraction as string) : null,
          createdAt: c.createdAt ? new Date(c.createdAt as string) : new Date(),
        }));

        setCustomers(parsedCustomers);
        setStats(data.stats);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load customers');
      } finally {
        setLoading(false);
      }
    };

    loadCustomers();
  }, [operator, selectedBusinessId]);

  // Filter customers by search query and tier
  const filteredCustomers = useMemo(() => {
    let result = customers;

    // Filter by tier
    if (tierFilter !== 'all') {
      result = result.filter((customer) => customer.customerTier === tierFilter);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((customer) =>
        customer.name.toLowerCase().includes(query) ||
        customer.phone.includes(query) ||
        (customer.email && customer.email.toLowerCase().includes(query)) ||
        (customer.tags || []).some(tag => tag.toLowerCase().includes(query))
      );
    }

    return result;
  }, [customers, searchQuery, tierFilter]);

  // Paginate filtered customers
  const paginatedCustomers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredCustomers.slice(start, start + pageSize);
  }, [filteredCustomers, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredCustomers.length / pageSize);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const formatDate = (date: Date | null) => {
    if (!date) return '-';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number, currency: string = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency || 'BRL',
    }).format(amount);
  };

  if (authLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!operator) {
    return null;
  }

  if (!permissions?.canSearchCustomers) {
    return (
      <Card className="text-center py-12">
        <CardContent className="flex flex-col items-center gap-4">
          <Lock className="h-12 w-12 text-muted-foreground" />
          <h2 className="text-xl font-semibold">{t('common.accessDenied')}</h2>
          <p className="text-muted-foreground max-w-md">
            {t('common.noPermission', { role: operator.role, action: t('customers.viewCustomers') })}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('customers.title')}</h1>
          <p className="text-muted-foreground">
            {canViewFullPII ? t('common.fullPIIAccess') : t('common.maskedPIIAccess')} | {filteredCustomers.length} {t('customers.customer', { count: filteredCustomers.length })}
          </p>
          <p className="text-muted-foreground mt-1">
            {t('common.viewing')}: <strong className="text-foreground">{selectedBusiness ? selectedBusiness.name : t('common.allBusinesses')}</strong>
          </p>
        </div>
        <Button onClick={() => setIsCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('customers.newCustomer')}
        </Button>
      </div>

      {/* CRM Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('customers.vipCustomers')}</p>
                  <p className="text-2xl font-bold">{stats.vipCount}</p>
                </div>
                <Star className="h-8 w-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('customers.newThisMonth')}</p>
                  <p className="text-2xl font-bold">{stats.newThisMonth}</p>
                </div>
                <UserPlus className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('customers.needsAttention')}</p>
                  <p className="text-2xl font-bold">{stats.needsAttention}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('customers.avgEngagement')}</p>
                  <p className="text-2xl font-bold">{(stats.avgEngagement ?? 0).toFixed(1)}%</p>
                </div>
                <TrendingUp className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {error && (
        <div className="bg-destructive/15 text-destructive px-4 py-3 rounded-md text-sm">
          {error}
        </div>
      )}

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('customers.searchPlaceholder')}
                className="pl-10"
              />
            </div>
            <Select value={tierFilter} onValueChange={(v) => setTierFilter(v as CustomerTier | 'all')}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder={t('customers.allTiers')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('customers.allTiers')}</SelectItem>
                <SelectItem value="VIP">{t('customers.vip')}</SelectItem>
                <SelectItem value="REGULAR">{t('customers.regular')}</SelectItem>
                <SelectItem value="NEW">{t('customers.new')}</SelectItem>
                <SelectItem value="AT_RISK">{t('customers.atRisk')}</SelectItem>
                <SelectItem value="CHURNED">{t('customers.churned')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger className="w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">{t('common.perPage', { count: 10 })}</SelectItem>
                <SelectItem value="20">{t('common.perPage', { count: 20 })}</SelectItem>
                <SelectItem value="50">{t('common.perPage', { count: 50 })}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Customers Table */}
      {loading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : filteredCustomers.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold">{t('customers.noCustomersFound')}</h3>
            <p className="text-muted-foreground">
              {searchQuery ? t('common.tryDifferentSearch') : t('customers.noCustomersYet')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('customers.customer')}</TableHead>
                    <TableHead>{t('customers.contact')}</TableHead>
                    <TableHead className="text-center">{t('customers.tier')}</TableHead>
                    <TableHead className="text-center">{t('customers.engagement')}</TableHead>
                    <TableHead className="text-center">{t('customers.bookings')}</TableHead>
                    <TableHead className="text-right">{t('customers.totalSpent')}</TableHead>
                    <TableHead>{t('customers.tags')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedCustomers.map((customer) => (
                    <TableRow
                      key={customer.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate({ to: `/operator/customers/${customer.id}` })}
                    >
                      <TableCell>
                        <div className="font-medium">
                          {canViewFullPII ? (
                            customer.name
                          ) : (
                            <Badge variant="secondary">{maskName(customer.name, false)}</Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {t('common.id')}: {customer.id.slice(0, 8)}...
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Phone className="h-3 w-3 text-muted-foreground" />
                            {canViewFullPII ? (
                              <span className="font-mono text-sm">{customer.phone}</span>
                            ) : (
                              <Badge variant="secondary" className="font-mono text-xs">
                                {maskPhone(customer.phone, false)}
                              </Badge>
                            )}
                          </div>
                          {customer.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="h-3 w-3 text-muted-foreground" />
                              {canViewFullPII ? (
                                <span className="text-sm">{customer.email}</span>
                              ) : (
                                <span className="text-sm text-muted-foreground">***@***.com</span>
                              )}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={getTierColorClasses(customer.tierColor)}>
                          {customer.tierLabel}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={`font-semibold ${getEngagementColor(customer.engagementScore ?? 0)}`}>
                          {(customer.engagementScore ?? 0).toFixed(1)}%
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{customer.bookingCount}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(customer.totalSpent ?? 0, customer.currency)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {(customer.tags || []).slice(0, 3).map((tag) => (
                            <Badge key={tag} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                          {(customer.tags || []).length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{customer.tags!.length - 3}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  {t('common.showingOf', { from: (currentPage - 1) * pageSize + 1, to: Math.min(currentPage * pageSize, filteredCustomers.length), total: filteredCustomers.length })}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    {t('common.previous')}
                  </Button>
                  <span className="text-sm text-muted-foreground px-2">
                    {t('common.pageOf', { current: currentPage, total: totalPages })}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    {t('common.next')}
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      {/* Create Customer Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('customers.newCustomer')}</DialogTitle>
            <DialogDescription>{t('customers.createDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('customers.phone')} *</Label>
              <Input
                placeholder="+5511999999999"
                value={createForm.phone}
                onChange={(e) => setCreateForm(prev => ({ ...prev, phone: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">{t('customers.phoneHint')}</p>
            </div>
            <div className="space-y-2">
              <Label>{t('customers.name')}</Label>
              <Input
                placeholder={t('customers.namePlaceholder')}
                value={createForm.name}
                onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
          </div>
          {createError && (
            <div className="bg-destructive/15 text-destructive px-3 py-2 rounded-md text-sm">
              {createError}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsCreateOpen(false); setCreateError(null); }}>{t('common.cancel')}</Button>
            <Button onClick={handleCreateCustomer} disabled={createLoading || !createForm.phone}>
              {createLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('customers.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export const Route = createFileRoute('/_auth/operator/customers/')({
  component: OperatorCustomersPage,
});
